import React from "react";
import { useTranslation } from "react-i18next";
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    InputBase,
    TextField,
    Autocomplete,
    Grid,
    Button,
    ClickAwayListener
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import MonthFilter from "../../../../../src/pages/reports-module/generic-filter-reports-component/billing-month-filter/month-filter";
import SelectionChip from "../../../../components/SelectionChip/SelectionChip";
import dayjs from "dayjs";

import {
    ORDER_TYPE_OPTIONS,
    TIME_PERIODS
} from "../../../../constants/PatientSurveyReportConstants";

const FiltersAccordion = ({
    filters,
    optionsLocation,
    selectedDate,
    validDate,
    isAccordionOpen,
    isGrid,
    setFilters,
    setAppliedFilters,
    setActiveTimeframe,
    setSelectedDate,
    setValidDate,
    setIsAccordionOpen,
    setDetailsPage,
    fetchSurveyReport,
    fetchSurveyDetails,
    formatBranchChip,
    handleChangePatientsFilter,
    style,
    styles,
    accordionRef
}) => {

    const toggleAccordion = () => {
        setIsAccordionOpen((prev) => !prev);
    };

    const { t } = useTranslation();
    const ALL_OPTION = { location_id: 0, location_name: t("reports.patientSurveyReportPage.filters.all") };

    return (
        <ClickAwayListener
            onClickAway={() => {
                if (isAccordionOpen) setIsAccordionOpen(false);
            }}
        >
        <div className={style.searchContainer1Style} ref={accordionRef}>
            <Accordion
                data-testid="toggleAccordion"
                className={style.customAccordion}
                expanded={isAccordionOpen}
                onChange={toggleAccordion}
            >
                <AccordionSummary
                    className={styles.accordionSummary}
                    expandIcon={<ExpandMoreIcon className={styles.expandIcon} />}
                    aria-controls="panel1a-content"
                    id="panel1a-header"
                    sx={{
                        minHeight: "48px",
                        "&.Mui-expanded": {
                            minHeight: "48px",
                        },
                        "& .MuiAccordionSummary-content": {
                            margin: "8px 0 !important",
                            display: "flex",
                            alignItems: "center",
                            columnGap: "8px",
                            rowGap: "6px"
                        },
                    }}
                >
                    <div className="searchFilterIcon">
                        <FilterAltIcon className={styles.filterIcon} />
                        {!filters.orderType && !filters.branch && !filters.timePeriod && (
                            <InputBase
                                placeholder={t("reports.patientSurveyReportPage.filters.searchFilters")}
                                className="filterIconInputBase"
                            />
                        )}
                    </div>

                    <div data-testid="searchSummary" className={styles.searchSummaryChips}>
                        <SelectionChip
                            label={t("reports.patientSurveyReportPage.filters.orderType")}
                            value={filters.orderType}
                            className={styles.filterChip}
                            deleteIcon={null}
                            onDelete={undefined}
                        />

                        {Array.isArray(filters.branch) && filters.branch.length > 0 ? (
                            filters.branch.map((branch) => (
                                <SelectionChip
                                    key={branch.location_id}
                                    label={t("reports.patientSurveyReportPage.filters.branch")}
                                    value={branch.location_name}
                                    className={styles.filterChip}
                                />
                            ))
                        ) : (
                            <SelectionChip
                                label={t("reports.patientSurveyReportPage.filters.branch")}
                                value={t("reports.patientSurveyReportPage.filters.all")}
                                className={styles.filterChip}
                            />
                        )}

                        <SelectionChip
                            label={t("reports.patientSurveyReportPage.filters.timePeriod")}
                            value={filters.timePeriod}
                            className={styles.filterChip}
                            deleteIcon={null}
                            onDelete={undefined}
                        />
                    </div>
                </AccordionSummary>

                    <AccordionDetails
                        className={style.customAccordionDetails}
                        sx={{ padding: "16px 16px 0px 16px !important" }}
                        onClick={(e) => e.stopPropagation()}
                    >

                    <div className={style.customMarginBottom}>
                        <TextField
                            select
                            fullWidth
                            label={t("reports.patientSurveyReportPage.filters.orderType")}
                            SelectProps={{ native: true, IconComponent: KeyboardArrowDownIcon }}
                            value={filters.orderType ?? "All"}
                            onChange={(e) =>
                                setFilters((prev) => ({ ...prev, orderType: e.target.value }))
                            }
                            size="small"
                        >
                            {ORDER_TYPE_OPTIONS.map(o => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                        </TextField>
                    </div>

                    <div className={style.customMarginBottom}>
                        <Autocomplete
                            multiple
                            id="branchFilter"
                            options={[ALL_OPTION, ...optionsLocation]}
                            value={filters.branch}
                            getOptionLabel={(opt) => opt.location_name}
                            isOptionEqualToValue={(option, value) =>
                                option.location_id === value.location_id
                            }
                            filterOptions={(option, state) => {
                                if (option.length > 0 && state.inputValue?.trim().length >= 3) {
                                    return option.filter((item) =>
                                        String(item.location_name)
                                            .toLowerCase()
                                            .includes(state.inputValue.trim().toLowerCase())
                                    );
                                }
                                return option;
                            }}
                            onChange={(_, newValue) =>
                                setFilters((prev) => ({
                                    ...prev,
                                    branch: newValue,
                                }))
                            }
                            limitTags={1}
                            renderTags={(value, getTagProps) => {
                                if (!value || value.length === 0) {
                                    return [
                                        <SelectionChip
                                            key="branch-all"
                                            value={t("reports.patientSurveyReportPage.filters.all")}
                                            size="small"
                                            onDelete={() =>
                                                setFilters((prev) => ({
                                                    ...prev,
                                                    branch: [],
                                                }))
                                            }
                                        />,
                                    ];
                                }

                                return value.map((option, index) => {
                                    const tagProps = getTagProps({ index });
                                    return (
                                        <SelectionChip
                                            key={tagProps.key}
                                            value={option.location_name}
                                            {...tagProps}
                                            size="small"
                                        />
                                    );
                                });
                            }}
                            renderInput={(params) => (
                                <TextField {...params} label={t("reports.patientSurveyReportPage.filters.branch")} size="small" fullWidth />
                            )}
                            size="small"
                            ChipProps={{ className: styles.branchChip }}
                        />
                    </div>

                    <div className={style.customMarginBottom}>
                        <Grid container spacing={2}>
                            <Grid item xs={filters.timePeriod === TIME_PERIODS.MONTHLY ? 6 : 12}>
                                <Autocomplete
                                    id="timePeriodFilter"
                                    disableClearable
                                    options={[
                                        t("reports.patientSurveyReportPage.filters.timePeriodOptions.monthly"),
                                        t("reports.patientSurveyReportPage.filters.timePeriodOptions.past3"),
                                        t("reports.patientSurveyReportPage.filters.timePeriodOptions.past6"),
                                        t("reports.patientSurveyReportPage.filters.timePeriodOptions.ytd"),
                                    ]}
                                    value={filters.timePeriod}
                                    onChange={(_, newValue) =>
                                        setFilters((prev) => ({
                                            ...prev,
                                            timePeriod: newValue || TIME_PERIODS.MONTHLY,
                                            month: newValue === TIME_PERIODS.MONTHLY
                                                ? (prev.month || dayjs(selectedDate).format("YYYY-MM"))
                                                : "",
                                        }))
                                    }
                                    renderInput={(params) => (
                                        <TextField {...params} label={t("reports.patientSurveyReportPage.filters.timePeriod")} size="small" fullWidth />
                                    )}
                                    size="small"
                                />
                            </Grid>

                            {filters.timePeriod === TIME_PERIODS.MONTHLY && (
                                <Grid item xs={6}>
                                    <MonthFilter
                                        validDate={validDate}
                                        setValidDate={setValidDate}
                                        selectedDate={selectedDate}
                                        setSelectedDate={setSelectedDate}
                                        handleChangePatientsFilter={handleChangePatientsFilter}
                                    />
                                </Grid>
                            )}
                        </Grid>
                    </div>

                    <div className={style.customMarginBottom}>
                        <Grid className={styles.actionsRow}>
                            <Button
                                color='secondary'
                                size="medium"
                                onClick={async () => {
                                    const newFilters = {
                                        orderType: "All",
                                        branch: [],
                                        timePeriod: TIME_PERIODS.MONTHLY,
                                        month: new Date().toISOString().slice(0, 7),
                                    };

                                    setFilters(newFilters);
                                    setAppliedFilters(newFilters);
                                    setActiveTimeframe({
                                        timePeriod: newFilters.timePeriod,
                                        month: newFilters.month
                                    });
                                    setSelectedDate(dayjs(newFilters.month, "YYYY-MM"));
                                    setValidDate(false);
                                    setIsAccordionOpen(false);

                                    await fetchSurveyReport(newFilters);

                                    if (!isGrid) {
                                        setDetailsPage(0);
                                        await fetchSurveyDetails(newFilters);
                                    }
                                }}
                            >
                                {t("reports.patientSurveyReportPage.filters.reset")}

                            </Button>

                            <Button
                                color='primary'
                                size="medium"
                                onClick={async () => {
                                    const newApplied = { ...filters };
                                    setAppliedFilters(newApplied);
                                    setActiveTimeframe({
                                        timePeriod: newApplied.timePeriod,
                                        month: newApplied.month
                                    });
                                    setIsAccordionOpen(false);

                                    await fetchSurveyReport(newApplied);

                                    if (!isGrid) {
                                        setDetailsPage(0);
                                        await fetchSurveyDetails(newApplied);
                                    }
                                }}
                            >
                                {t("reports.patientSurveyReportPage.filters.search")}

                            </Button>
                        </Grid>
                    </div>
                </AccordionDetails>
            </Accordion>
            </div>
        </ClickAwayListener>
    );
};

export default FiltersAccordion;
